import './Header.css';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import useScroll from 'Hooks/useScroll';
import { Logo } from 'Components/Logo';
import { SmartLink } from 'Components/SmartLink';
import UserMenu from 'Features/UserMenu';
import { RedfastZoneByPathname } from 'Components/RedfastZone';
import { HTMLParser } from 'Utils/Parser';
import { MobileWebConfirmationHeader } from 'Components/MobileWebConfirmationHeader/MobileWebConfirmationHeader';
import { device as isMobile } from 'device.js';
import { isDefaultPlanFlow } from 'Utils/Analytics/Amplitude';
import { LinkSubmitButton } from 'Components/Fields/Buttons';
import { VA_QUERY_PARAM } from 'Utils/Constants';
import { isFreeTrial } from 'Utils/Plans';

const getHeaderRouteStateByPathname = (pathname = '') => ({
  isPurchaseFunnelRoute: /signup|signin|shipping|payment/g.test(pathname) && !/signup-confirmation/g.test(pathname),
  isGiftingRoute: /review-and-redeem|redeem-confirmation/g.test(pathname),
  isPromoBannerRoute: /signup-plans|shipping|payment/g.test(pathname),
  isNoSignInButtonRoute: /reset-by-email|mvpd|bango-final/g.test(pathname),
  isNoSignUpButtonRoute: /signup-plans/g.test(pathname),
});

//Per sign up flow redesign experiment. If becomes permanent, add to isNoSignUpButtonRoute above
const noSignUpButtonPerRedesign = (pathname = '') => isDefaultPlanFlow() && /signup-account/g.test(pathname);

export const trackGlobalClick = (itemTitle, props = {}) => {
  trackEvent(TRACKING_ENUMS.EVENT_GLOBAL_NAV_CLICK, {
    page_item_title: itemTitle,
    ...props,
  });
};

const trackUpgradeClick = (text) => {
  trackEvent(TRACKING_ENUMS.EVENT_UPGRADE_CLICKED, {
    page_item_title: text,
    page_action_location: 'global nav',
  });
  trackEvent(TRACKING_ENUMS.EVENT_PURCHASE_CTA_CLICKED, {
    page_button_text: text,
    page_action_location: 'global nav',
    page_item_title: 'join fox nation',
  });
};

export function HeaderComponent() {
  // state
  const [showPromoBanner, setPromoBanner] = useState(false);
  const [showSignUpButton, setSignUpButton] = useState(false);
  const { scrolledClassName } = useScroll({ className: 'headerScrolled' });
  const { pathname = '', search = '' } = useLocation();
  //need to check entire search since the query param can be in a return_url query param
  const isVeteransAdv = !!search.includes(`?${VA_QUERY_PARAM}=`);

  // redux selector
  const user = useSelector((s) => s.user);
  const purchaseMode = useSelector((s) => s.purchaseMode);
  const locale = useSelector((s) => s.appConfig.strings);
  const defaultConfig = useSelector((s) => s.appConfig.defaultConfig);
  const routes = useSelector((s) => s.appConfig.routes);
  const pageMeta = useSelector((s) => s.appConfig.pageMeta);
  const pageLayout = useSelector((s) => s.pageLayout);
  const secondScreen = useSelector((s) => s.secondScreen);
  const bango = useSelector((s) => s.integrations.bango);
  const allPlans = useSelector((s) => s.plans.allPlans);

  const { device, isDeviceActivation } = secondScreen;
  const { onHeaderClose } = pageLayout;
  const { requirePlanSelection = true } = purchaseMode;
  const { showUserMenuOnPurchaseFlow, rootDomain, showHeaderSignUp } = defaultConfig;
  const isAuthenticated = user.isAuthenticated || user.hasMvpd;
  const isBangoFlow = !!bango.subscriptionId && !user.hasEntitlements;

  const { isPurchaseFunnelRoute, isGiftingRoute, isPromoBannerRoute, isNoSignInButtonRoute, isNoSignUpButtonRoute } =
    useMemo(() => getHeaderRouteStateByPathname(pathname), [pathname]);

  const hasNoSignUpButtonPerRedesign = useMemo(() => noSignUpButtonPerRedesign(pathname), [pathname]);

  // sign up button
  useEffect(() => {
    const mayShowSignUpButton =
      !isAuthenticated &&
      !onHeaderClose &&
      !isGiftingRoute &&
      !isDeviceActivation &&
      !isNoSignInButtonRoute &&
      !isBangoFlow &&
      !isVeteransAdv &&
      !hasNoSignUpButtonPerRedesign &&
      !isNoSignUpButtonRoute &&
      !!allPlans.length;
    const showSignUpButton = showHeaderSignUp && requirePlanSelection && mayShowSignUpButton;
    setSignUpButton(showSignUpButton);
  }, [
    isAuthenticated,
    onHeaderClose,
    showHeaderSignUp,
    requirePlanSelection,
    isGiftingRoute,
    isDeviceActivation,
    isNoSignInButtonRoute,
    isBangoFlow,
    isNoSignUpButtonRoute,
    hasNoSignUpButtonPerRedesign,
    isVeteransAdv,
    allPlans.length,
  ]);

  // promo banner
  useEffect(() => {
    const showPromoBanner = locale.headerPromoBanner && isPromoBannerRoute;
    setPromoBanner(showPromoBanner);
  }, [pageLayout, locale, isPromoBannerRoute]);

  // headerClass
  const headerClass = useMemo(() => {
    const promoBannerClass = showPromoBanner ? 'promoBanner' : '';
    return [scrolledClassName, promoBannerClass].filter(Boolean).join(' ');
  }, [scrolledClassName, showPromoBanner]);

  const signUpButtonText = useMemo(() => {
    const hasPlanWithFreeTrial = allPlans.find((p) => isFreeTrial(p));

    return hasPlanWithFreeTrial ? 'Start Your Free Trial' : 'Join Now';
  }, [allPlans]);

  const isSignUp = pathname.includes('/account/signup-plans');
  const isSignUpAccount = pathname.includes('/account/signup-account');
  const variantTitle = 'Choose a Plan';

  const trialRoute = useMemo(() => {
    let route = routes.signupPlans;
    if (isDefaultPlanFlow()) {
      route = routes.signupCreateAccount;
    } else if (allPlans.length === 1) {
      route = `${routes.signupCreateAccount}/${allPlans[0]?.planId}`;
    }
    return route;
  }, [allPlans, routes]);

  const isSheerVerification = pathname.includes(routes.sheerIdVerification);

  if (isMobile.mobile && pathname.includes('/account/signup-confirmation')) {
    return <MobileWebConfirmationHeader />;
  }

  return (
    <Fragment>
      <header className={`globalHeader ${headerClass}`}>
        <div className="globalHeaderCenter">
          <div className="headerSideLeft">
            {!isBangoFlow && (
              <>
                <SmartLink
                  linkUrl={rootDomain}
                  text={<Logo className="headerLogo" />}
                  className="headerLogoLink hasVariant"
                  onClick={() => trackGlobalClick('Network Logo')}
                  reload
                />
                <SmartLink
                  linkUrl={rootDomain}
                  text={<Logo className="headerLogoVariant" isVariant />}
                  className="headerLogoVariantLink"
                  onClick={() => trackGlobalClick('Network Logo')}
                  reload
                />
              </>
            )}
            {isBangoFlow && (
              <>
                <div className="headerLogoLink hasVariant">
                  <Logo className="headerLogo" />
                </div>
                <div className="headerLogoVariantLink">
                  <Logo className="headerLogoVariant" isVariant />
                </div>
              </>
            )}
          </div>
          <div className="headerCenter"></div>
          <div className="headerSideRight">
            {showSignUpButton && (
              <LinkSubmitButton
                className="headerSignupButton"
                modernStyle={true}
                inline={true}
                path={{ pathname: trialRoute, state: { page_source: 'global nav' } }}
                onClick={() => trackUpgradeClick(signUpButtonText)}
                text={signUpButtonText}
              />
            )}
            {pathname !== routes.signin &&
              (!isSheerVerification || (!isBangoFlow && isSheerVerification)) &&
              showUserMenuOnPurchaseFlow &&
              !isDeviceActivation &&
              !onHeaderClose &&
              !isNoSignInButtonRoute &&
              !(isPurchaseFunnelRoute && isBangoFlow) && (
                <UserMenu
                  segmentProps={{ page_start_source: 'global nav' }}
                  pathname={pathname}
                  locationSearch={search}
                />
              )}
            {isSignUp && !isAuthenticated && (
              <div className="variantTitleContainer">
                <h1 className="variantTitle">{variantTitle}</h1>
              </div>
            )}
            {onHeaderClose && (
              <button className="headerClose" onClick={onHeaderClose} type="button">
                <img src="/account/static/images/FN/close.svg" width="24" height="24" alt="Close" />
              </button>
            )}
            {isDeviceActivation && (
              <div className="secondScreen">
                Activate
                {device && (
                  <img
                    className="device"
                    src={`/account/static/images/deviceIcons/secondScreen/${device}.png`}
                    alt="Device"
                  />
                )}
              </div>
            )}
            {pageMeta.chatURL && (
              <div className="headerFaq">
                <SmartLink linkUrl={pageMeta.chatURL} text="faq" className="headerFaqBtn" target="_blank" reload />
              </div>
            )}
          </div>
        </div>
        {showPromoBanner && <div className="globalHeaderPromoBanner">{HTMLParser(locale.headerPromoBanner)}</div>}
      </header>
      <RedfastZoneByPathname />
    </Fragment>
  );
}
const Header = HeaderComponent;
export default Header;
